import { Link, Navigate } from "react-router-dom";
import { BellRing, KanbanSquare, MessageCircle, CalendarCheck, Sparkles, FileCheck2, ShieldCheck, Wallet, Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ProductTour } from "@/components/ProductTour";

const FEATURES = [
  { icon: BellRing, t: "Nessun richiamo dimenticato", d: "Dashboard del giorno, notifiche, riepilogo email alle 8:00 con il consiglio AI su chi chiamare per primo." },
  { icon: KanbanSquare, t: "Pratiche sotto controllo", d: "Mutui, prestiti, cessioni del quinto, polizze e conti in una pipeline visiva con rata stimata." },
  { icon: CalendarCheck, t: "Prenotazioni online", d: "Il tuo link personale: i clienti fissano da soli l'appuntamento, anche chattando con l'assistente AI." },
  { icon: FileCheck2, t: "Documenti dal cliente", d: "Checklist per prodotto, caricamento dal link sicuro e solleciti automatici." },
  { icon: ShieldCheck, t: "Privacy firmata online", d: "Informativa GDPR e consensi con firma elettronica tramite codice OTP e PDF con registro delle prove." },
  { icon: MessageCircle, t: "WhatsApp, SMS ed email", d: "Modelli personalizzati e bozze scritte dall'AI, inviate in un clic." },
  { icon: Sparkles, t: "Rinnovi automatici", d: "Compleanni, scadenze polizze, rinnovo cessione del quinto e fine tasso fisso: ti avvisiamo noi." },
  { icon: Wallet, t: "Provvigioni", d: "Report mensile per prodotto e banca, con le provvigioni previste in pipeline." },
];

const INCLUDED = ["Clienti e pratiche illimitati", "Pagina prenotazione + assistente AI", "Caricamento documenti e firma privacy", "Sincronizzazione Google Calendar", "Email automatiche e riepilogo giornaliero", "Importazione da Excel"];

const FAQ = [
  ["Devo pagare subito?", "No. Inserisci la carta all'iscrizione, ma il primo addebito avviene solo dopo 30 giorni. Puoi disdire prima e non paghi nulla."],
  ["Posso disdire quando voglio?", "Sì, dalla pagina Abbonamento con un clic. L'abbonamento resta attivo fino alla fine del periodo pagato."],
  ["Cosa succede ai miei dati se smetto di pagare?", "Per 15 giorni puoi consultarli in sola lettura; poi l'accesso viene sospeso ma i dati restano conservati e tornano disponibili alla riattivazione."],
  ["I prezzi includono l'IVA?", "No, i prezzi sono IVA esclusa. Ricevi regolare fattura."],
];

export const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Cta = ({ id, children, light }) => (
  <button data-testid={id} onClick={googleLogin} className={`inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-medium transition-colors ${light ? "bg-white text-slate-900 hover:bg-sky-100" : "bg-sky-700 text-white hover:bg-sky-800"}`}>
    {children} <ArrowRight className="h-4 w-4" />
  </button>
);

const Pricing = () => (
  <section id="prezzi" className="px-6 lg:px-16 py-20 bg-white" data-testid="landing-pricing">
    <h2 className="font-display text-3xl font-bold tracking-tight">Un solo piano, tutto incluso</h2>
    <p className="text-slate-500 mt-2">30 giorni gratis, poi scegli mensile o annuale. Prezzi IVA esclusa.</p>
    <div className="grid md:grid-cols-2 gap-6 mt-10 max-w-4xl">
      {[["Mensile", "69", "/mese", "Disdici quando vuoi", "price-monthly"], ["Annuale", "690", "/anno", "2 mesi gratis rispetto al mensile", "price-yearly"]].map(([t, p, u, n, id], i) => (
        <div key={t} data-testid={id} className={`rounded-2xl border p-8 ${i ? "border-sky-500 ring-2 ring-sky-100" : "border-slate-200"}`}>
          <div className="flex items-center justify-between"><p className="font-display font-semibold text-lg">{t}</p>{i === 1 && <span className="rounded-full bg-sky-100 text-sky-700 text-xs font-semibold px-3 py-1">Più conveniente</span>}</div>
          <p className="mt-4"><span className="font-display text-5xl font-bold">€{p}</span><span className="text-slate-500">{u} + IVA</span></p>
          <p className="text-sm text-slate-500 mt-1">{n}</p>
          <ul className="mt-6 space-y-2">{INCLUDED.map((x) => <li key={x} className="flex gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />{x}</li>)}</ul>
          <div className="mt-8"><Cta id={`pricing-cta-${i}`}>Prova gratis 30 giorni</Cta></div>
        </div>
      ))}
    </div>
  </section>
);

export default function Landing() {
  const { user, loading } = useAuth();
  if (!loading && user) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="landing-page">
      <header className="flex items-center justify-between px-6 lg:px-16 py-5">
        <span data-testid="brand-logo" className="flex items-center gap-2.5 font-display text-xl font-bold text-[#0B3BA8]"><img src="/logo-icon.png" alt="" className="h-9 w-9 object-contain" />Kreditofacile</span>
        <nav className="flex items-center gap-6 text-sm">
          <a href="#prezzi" className="hidden sm:inline hover:text-sky-700">Prezzi</a>
          <button data-testid="google-login-btn" onClick={googleLogin} className="rounded-full border border-slate-300 bg-white px-5 py-2 font-medium hover:border-sky-500 transition-colors">Accedi con Google</button>
        </nav>
      </header>
      <main>
      <section className="relative overflow-hidden bg-[#0F172A] text-white px-6 lg:px-16 py-24 mx-3 lg:mx-6 rounded-3xl">
        <div className="absolute inset-0 opacity-[0.07] grain" style={{ filter: "invert(1)" }} />
        <div className="relative max-w-3xl fade-up">
          <p className="text-sky-300 text-sm font-medium tracking-wide uppercase">Il CRM per agenti in attività finanziaria</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4 leading-[1.05]">Ogni nominativo, ogni richiamo, ogni pratica. Senza dimenticare nulla.</h1>
          <p className="text-slate-300 text-base md:text-lg mt-6 max-w-2xl">Mutui, prestiti, cessioni del quinto e polizze: Kreditofacile organizza la tua giornata, fa prenotare i clienti da soli e ti ricorda rinnovi e scadenze.</p>
          <div className="flex flex-wrap items-center gap-4 mt-10">
            <Cta id="hero-cta" light>Prova gratis per 30 giorni</Cta>
            <a href="#tour" data-testid="hero-tour-link" className="rounded-full border border-white/30 px-6 py-3 text-sm font-medium hover:bg-white/10 transition-colors">Guarda come funziona</a>
            <span className="text-sm text-slate-400">Primo addebito dopo 30 giorni · disdici quando vuoi</span>
          </div>
        </div>
      </section>
      <ProductTour />
      <section className="px-6 lg:px-16 py-20">
        <h2 className="font-display text-3xl font-bold tracking-tight max-w-xl">Tutto quello che serve al tuo lavoro quotidiano</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {FEATURES.map(({ icon: I, t, d }, i) => (
            <div key={t} className="fade-up rounded-2xl bg-white border border-slate-200/80 p-6 hover:border-sky-300 transition-colors" style={{ animationDelay: `${i * 60}ms` }}>
              <I className="h-6 w-6 text-sky-600" />
              <p className="font-display font-semibold mt-4">{t}</p>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <Pricing />
      <section className="px-6 lg:px-16 py-20" data-testid="landing-faq">
        <h2 className="font-display text-3xl font-bold tracking-tight">Domande frequenti</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-6 max-w-5xl">
          {FAQ.map(([q, a]) => <div key={q}><p className="font-semibold">{q}</p><p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{a}</p></div>)}
        </div>
      </section>
      </main>
      <footer className="px-6 lg:px-16 py-10 border-t border-slate-200 flex flex-wrap gap-6 text-sm text-slate-500">
        <span>© {new Date().getFullYear()} Kreditofacile</span>
        <a href="https://www.iubenda.com/termini-e-condizioni/64612498" title="Termini e Condizioni" data-testid="footer-terms" className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed hover:text-sky-700">Termini e Condizioni</a>
        <Link to="/dpa" data-testid="footer-dpa" className="hover:text-sky-700">Accordo trattamento dati (DPA)</Link>
        <a href="https://www.iubenda.com/privacy-policy/64612498" title="Privacy Policy" data-testid="footer-privacy" className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed hover:text-sky-700">Privacy Policy</a>
        <a href="https://www.iubenda.com/privacy-policy/64612498/cookie-policy" title="Cookie Policy" data-testid="footer-cookie" className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed hover:text-sky-700">Cookie Policy</a>
      </footer>
    </div>
  );
}
